import React from 'react'
import { mdx } from '@mdx-js/react'
/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Learn how to identify and prevent duplicate Items. Identifying duplicate Items prevents excess billing and confusing application behavior."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Preventing duplicate Items' subtitle='Prevent unnecessary billing and confusing application behavior' mdxType="Header" />
    <YouTubeEmbed videoId='6wWPKrVPaio' alignment='full' title='Preventing duplicate Items' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "how-duplicate-items-are-created"
    }}>{`How duplicate Items are created`}</h4>
    <p>{`An `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/#item"
      }}>{`Item`}</a>{` represents a login at a financial institution. This login typically happens when an end user links an account (or several accounts) using `}<a parentName="p" {...{
        "href": "/docs/link/"
      }}>{`Plaid Link`}</a>{`. A duplicate Item will be created if the end user logs into the same institution with the same credentials a second time through Plaid Link (in the same application), and if an access token is requested for the Item.`}</p>
    <p>{`Duplicate Items can occur for multiple reasons. For example, a duplicate Item can occur if a user accidentally links the same account more than once, because they do not realize they already linked an account, or because their linked account is no longer working. Duplicate Items can also occur if a user intentionally links multiple Items for abusive purposes (for example, as part of an attempt to receive multiple sign-up bonuses or to evade a ban). `}</p>
    <p>{`Duplicate Items can create confusing or unwanted behavior in your application, can sometimes be a vector for fraud and abuse, and may cause you to be charged multiple times for the same Item. We recommend building safeguards in your application to help prevent end users from creating duplicate Items. In this article, we'll describe a few ways to prevent and detect Item duplication.`}</p>
    <h4 {...{
      "id": "preventing-duplicate-items"
    }}>{`Preventing duplicate Items`}</h4>
    <h5 {...{
      "id": "use-the-onsuccess-callback-metadata"
    }}>{`Use the onSuccess callback metadata`}</h5>
    <p>{`The `}<a parentName="p" {...{
        "href": "/docs/link/web/#onsuccess"
      }}><inlineCode parentName="a">{`onSuccess`}</inlineCode></a>{` callback is called when a user successfully links an Item using Plaid Link. This callback provides metadata that you can use to prevent duplicate Items from being created. (Alternatively, if you're using a connection method that does not provide data in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback, such as Hosted Link, you can obtain this metadata by calling `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` and using the `}<inlineCode parentName="p">{`results.item_add_results`}</inlineCode>{` array.) One approach is to require a user login prior to launching Plaid Link so that you can retrieve existing Items associated with the user.`}</p>
    <p>{`Then, before requesting an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, examine and compare the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback metadata to the user's existing Items. You can compare a combination of the accounts' `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`, account `}<inlineCode parentName="p">{`name`}</inlineCode>{`, and account `}<inlineCode parentName="p">{`mask`}</inlineCode>{` to determine whether an end user has previously linked an account to your application. Do not exchange a public token for an access token if you detect a duplicate Item.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`While the `}<inlineCode parentName="p">{`mask`}</inlineCode>{` value is usually the same as the last 4 digits of the account number, this is not the case at all institutions. Never detect duplicate Items by attempting to match a `}<inlineCode parentName="p">{`mask`}</inlineCode>{` with an account number.`}</p>
    </Callout>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #Sample onSuccess metadata schema",
        "switcher": "false",
        "#Sample": true,
        "onSuccess": true,
        "metadata": true,
        "schema": true
      }}>{`{
  institution: {
    name: 'Wells Fargo',
    institution_id: 'ins_4'
  },
  accounts: [
    {
      id: 'ygPnJweommTWNr9doD6ZfGR6GGVQy7fyREmWy',
      name: 'Plaid Checking',
      mask: '0000',
      type: 'depository',
      subtype: 'checking',
      verification_status: null
    },
    {
      id: '9ebEyJAl33FRrZNLBG8ECxD9xxpwWnuRNZ1V4',
      name: 'Plaid Saving',
      mask: '1111',
      type: 'depository',
      subtype: 'savings'
    }
    ...
  ],
  link_session_id: '79e772be-547d-4c9c-8b76-4ac4ed4c441a'
}
`}</code></pre>
    <p>{`Note that duplicate Items are not always identical. For example, an Item may have a checking account linked, while its duplicate may have only a savings account linked. While this scenario is rare, if it becomes a problem for your use case, you can switch to using the `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{` field on a per-user basis to prevent duplicate Items and enforce that each user of your app does not link multiple Items with the same institution. However, because this is a legitimate use case for some applications, it is not recommended as the primary means of detecting duplicate Items.`}</p>
    <Callout warning title='Duplicate Items at Chase, PNC, NFCU, and Schwab' mdxType="Callout">
      <p>{`For Chase, PNC, Navy Federal Credit Union, and Charles Schwab, existing Items may be invalidated if an end user adds a second Item using the same credentials, at the point when the user has completed the institution's OAuth flow. For the aforementioned providers other than Charles Schwab, this will only happen if the second Item does not have exactly the same set of accounts associated as the first Item (i.e. the user granted permissions to different accounts in the institution's OAuth flow).`}</p>
      <p>{`If this occurs, delete the old Item using `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` and use the new Item. Alternatively, the old Item can be repaired using `}<a parentName="p" {...{
          "href": "/docs/link/update-mode"
        }}>{`update mode`}</a>{`, which will invalidate the new Item. Then you may delete the new Item.`}</p>
      <p>{`For Auth or Transfer customers, Chase or PNC Item invalidation caused by creating a duplicate Item will not invalidate or change the Item's TAN. `}</p>
    </Callout>
    <h5 {...{
      "id": "implement-pre-link-messaging"
    }}>{`Implement Pre-Link messaging`}</h5>
    <p>{`A lightweight but effective method for preventing accidental duplicate Items from being created is by providing relevant messaging and information to end users before they engage with Plaid Link in your application. For example, displaying a list of accounts they've already connected to your application can help prevent end users from inadvertently linking the same accounts again.`}</p>
    <h5 {...{
      "id": "use-links-update-mode"
    }}>{`Use Link's update mode`}</h5>
    <p>{`From time to time, an Item may become unhealthy due to entering the `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` state. Or, the Item may still be healthy, but you may want your end user to authorize additional accounts or permissions associated with the Item. When either of these scenarios happens, use `}<a parentName="p" {...{
        "href": "/docs/link/update-mode/"
      }}>{`Link in update mode`}</a>{` to refresh the Item instead of creating a new Item.`}</p>
    <h4 {...{
      "id": "example-implementation-preventing-accidental-duplicate-items"
    }}>{`Example implementation: Preventing accidental duplicate Items`}</h4>
    <p>{`For an example that demonstrates how to prevent accidental duplicate Items, see the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern"
      }}>{`Plaid Pattern`}</a>{` sample app. Plaid Pattern implements simple server-side logic to check whether the user ID and institution ID pair already exist in the application database. If the pair exists, an access token will not be requested for this Item, thereby preventing a duplicate. In addition, a message is displayed to the end user informing them that they've already linked this Item. The relevant code can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/server/routes/items.js#L41-L49"
      }}>{`/server/routes/items.js`}</a>{` and `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/server/db/queries/items.js#L73-L88"
      }}>{`/server/db/queries/items.js`}</a>{`.`}</p>
    <h4 {...{
      "id": "identifying-existing-duplicate-items"
    }}>{`Identifying existing duplicate Items`}</h4>
    <p>{`To identify existing duplicate Items, use the same matching logic as described above, but retrieve this data via the `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{` endpoint instead of the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. Occasionally, the `}<inlineCode parentName="p">{`mask`}</inlineCode>{` or `}<inlineCode parentName="p">{`name`}</inlineCode>{` fields may be null, in which case you can compare `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` as a fallback. After identifying duplicate Items, use the `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` endpoint to delete an Item.`}</p>
    <p>{`If you are using Auth via `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, existing duplicate Items may be detected by using the account and routing number fields and looking for duplicates. This method will not work for Items at institutions that use `}<a parentName="p" {...{
        "href": "/docs/auth/#tokenized-account-numbers"
      }}>{`Tokenized Account Numbers (TANs)`}</a>{`. To detect existing duplicate Items at these institutions, you can use the `}<a parentName="p" {...{
        "href": "/docs/api/accounts/#accounts-get-response-accounts-persistent-account-id"
      }}><inlineCode parentName="a">{`persistent_account_id`}</inlineCode></a>{` field, which will be the same across duplicate instances of the Item.`}</p>
    <h4 {...{
      "id": "detecting-and-preventing-duplicate-items-across-user-accounts"
    }}>{`Detecting and preventing duplicate Items across user accounts`}</h4>
    <p>{`When attempting to detect or prevent duplicate Items, your approach should depend on the type of duplicate Item you are trying to prevent. For example, you can often scope your search to either a single user's accounts, or to all accounts known to your application. If you are attempting to prevent accidental duplicate Items, you should scope the search to a single user; if you are attempting to detect or prevent abuse, it may make more sense to expand your search to all accounts that have been linked to your app. `}</p>
    <p>{`Note that there can be legitimate use cases for the same financial institution account to be linked across multiple different user accounts (for example, family members who share a joint bank account, or employees of the same company who share access to a business account); depending on your use case, you may want to incorporate this data into a broader abuse detection framework rather than blocking all duplicate accounts.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "how-duplicate-items-are-created",
        level: 4,
        title: "How duplicate Items are created",
      },
{
        id: "preventing-duplicate-items",
        level: 4,
        title: "Preventing duplicate Items",
      },
{
        id: "example-implementation-preventing-accidental-duplicate-items",
        level: 4,
        title: "Example implementation: Preventing accidental duplicate Items",
      },
{
        id: "identifying-existing-duplicate-items",
        level: 4,
        title: "Identifying existing duplicate Items",
      },
{
        id: "detecting-and-preventing-duplicate-items-across-user-accounts",
        level: 4,
        title: "Detecting and preventing duplicate Items across user accounts",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
